import { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw, HardDrive, Activity, Clock, TrendingUp, BarChart3, AlertTriangle, Database,
} from 'lucide-react';
import { fetchPerformanceMetrics, type PerformanceMetrics } from '@/api/monitoring';
import { useErrorToast, LoadRetry } from '@/lib/notify';

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}G`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtBytes(bytes: number): string {
  if (bytes >= 1 << 30) return `${(bytes / (1 << 30)).toFixed(1)} GB`;
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${(bytes / (1 << 10)).toFixed(1)} KB`;
  return `${bytes} B`;
}
function fmtDuration(seconds: number): string {
  if (seconds <= 0) return '0 sn';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}g ${hours}sa`;
  if (hours > 0) return `${hours}sa ${minutes}dk`;
  if (minutes > 0) return `${minutes}dk`;
  return `${seconds}sn`;
}

// ── Card primitives ───────────────────────────────────────────────────────────
type Status = 'ok' | 'warn' | 'error' | 'info' | 'muted';

const STATUS_COLORS: Record<Status, { card: string; icon: string; value: string }> = {
  ok:    { card: 'border-emerald-100 bg-emerald-50/40',  icon: 'text-emerald-500', value: 'text-emerald-700' },
  warn:  { card: 'border-amber-100   bg-amber-50/40',    icon: 'text-amber-500',   value: 'text-amber-700'   },
  error: { card: 'border-rose-100    bg-rose-50/40',     icon: 'text-rose-500',    value: 'text-rose-700'    },
  info:  { card: 'border-blue-100    bg-blue-50/40',     icon: 'text-blue-500',    value: 'text-blue-700'    },
  muted: { card: 'border-gray-100    bg-gray-50',        icon: 'text-gray-400',    value: 'text-gray-700'    },
};

function MetricCard({
  icon, label, value, sub, status = 'muted', progress, action,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  status?: Status;
  progress?: number;
  action?: React.ReactNode;
}) {
  const c = STATUS_COLORS[status];
  return (
    <div className={`rounded-lg border p-2.5 flex flex-col gap-1 ${c.card}`}>
      <div className="flex items-center justify-between">
        <span className={`${c.icon}`}>{icon}</span>
        <div className="flex items-center gap-1">
          {action}
          {status !== 'muted' && (
            <span className={`text-[10px] font-medium px-1 py-0.5 rounded-full ${
              status === 'ok'    ? 'bg-emerald-100 text-emerald-700' :
              status === 'warn'  ? 'bg-amber-100   text-amber-700'   :
              status === 'error' ? 'bg-rose-100    text-rose-700'    :
                                   'bg-blue-100    text-blue-700'
            }`}>
              {status === 'ok' ? 'İyi' : status === 'warn' ? 'Dikkat' : status === 'error' ? 'Kritik' : 'Bilgi'}
            </span>
          )}
        </div>
      </div>
      <div>
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className={`text-sm font-bold leading-snug ${c.value}`}>{value}</p>
        {sub && <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{sub}</p>}
      </div>
      {progress != null && (
        <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              progress >= 90 ? 'bg-rose-400' : progress >= 70 ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function Panel({ title, icon, subtitle, children }: { title: string; icon: React.ReactNode; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-50/50 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-blue-500">{icon}</span>
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-tight">{title}</h2>
        </div>
        {subtitle && <p className="text-[11px] text-gray-400 font-medium">{subtitle}</p>}
      </div>
      <div className="p-5 flex flex-col gap-6">{children}</div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-widest px-0.5">
        {icon}
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

export default function ServerTab() {
  const [data, setData]       = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  useErrorToast(error);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await fetchPerformanceMetrics();
      setData(d);
      setLastRefresh(new Date());
      setError('');
    } catch {
      setError('Metrikler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  if (loading) return <div className="flex items-center justify-center h-60 text-gray-400 text-sm">Yükleniyor...</div>;
  if (error)   return <LoadRetry />;
  if (!data)   return null;

  const { server } = data;
  const { pool } = data;
  const loadPerCpuPct = server.cpu_count > 0 ? Math.round((server.load_avg_1 / server.cpu_count) * 100) : 0;
  const readBytes = server.io.sectors_read * 512;
  const writeBytes = server.io.sectors_written * 512;

  return (
    <div className="flex flex-col gap-4 pb-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-xs text-gray-400">
          {lastRefresh && <span>Son güncelleme: {lastRefresh.toLocaleTimeString('tr-TR')}</span>}
        </div>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} /> Yenile
        </button>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <div
            onClick={() => setAutoRefresh(p => !p)}
            className={`relative w-10 h-5 rounded-full transition-colors ${autoRefresh ? 'bg-blue-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoRefresh ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <span className={autoRefresh ? 'text-blue-600 font-medium' : 'text-gray-500'}>Otomatik (5s)</span>
        </label>
      </div>

      <Panel
        title="Server"
        icon={<Activity size={14} />}
        subtitle="Tek sunuculu kurulum için işletim sistemi, bellek, disk ve I/O görünümü"
      >
        <Section title="DB Pool (Backend)" icon={<Database size={12} />}>
          <MetricCard
            icon={<Database size={14} />}
            label="Pool Toplam"
            value={pool.total}
            sub={`Max ${pool.max}`}
            status="info"
            progress={pool.max > 0 ? Math.round((pool.total / pool.max) * 100) : undefined}
          />
          <MetricCard
            icon={<Activity size={14} />}
            label="Aktif Connection"
            value={pool.active}
            sub="Kullanımdaki bağlantılar"
            status={pool.active >= pool.max ? 'error' : pool.active >= Math.round(pool.max * 0.8) ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<Clock size={14} />}
            label="Idle Connection"
            value={pool.idle}
            sub="Havuzda bekleyen bağlantılar"
            status="muted"
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="Waiting Queue"
            value={pool.waiting}
            sub="Pool boşalmasını bekleyen istek"
            status={pool.waiting > 0 ? 'warn' : 'ok'}
          />
        </Section>

        <Section title="Sunucu Durumu" icon={<Activity size={12} />}>
          <MetricCard
            icon={<Activity size={14} />}
            label="Host"
            value={server.hostname}
            sub={`${server.platform} ${server.release}`}
            status="info"
          />
          <MetricCard
            icon={<Clock size={14} />}
            label="Uptime"
            value={fmtDuration(server.uptime_seconds)}
            sub={`${server.cpu_count} CPU çekirdeği`}
            status="muted"
          />
          <MetricCard
            icon={<BarChart3 size={14} />}
            label="Load Avg (1 dk)"
            value={server.load_avg_1.toFixed(2)}
            sub={`5 dk ${server.load_avg_5.toFixed(2)} / 15 dk ${server.load_avg_15.toFixed(2)}`}
            status={loadPerCpuPct >= 90 ? 'error' : loadPerCpuPct >= 70 ? 'warn' : 'ok'}
            progress={loadPerCpuPct}
          />
        </Section>

        <Section title="Memory" icon={<Database size={12} />}>
          <MetricCard
            icon={<Database size={14} />}
            label="RAM Kullanımı"
            value={fmtBytes(server.memory.used_bytes)}
            sub={`${fmtBytes(server.memory.free_bytes)} boş / ${fmtBytes(server.memory.total_bytes)} toplam`}
            status={server.memory.used_pct >= 90 ? 'error' : server.memory.used_pct >= 75 ? 'warn' : 'ok'}
            progress={server.memory.used_pct}
          />
          <MetricCard
            icon={<Database size={14} />}
            label="Swap Kullanımı"
            value={server.swap.total_bytes > 0 ? fmtBytes(server.swap.used_bytes) : 'Kapalı'}
            sub={server.swap.total_bytes > 0
              ? `${fmtBytes(server.swap.free_bytes)} boş / ${fmtBytes(server.swap.total_bytes)} toplam`
              : 'Swap alanı tanımlı değil'}
            status={server.swap.total_bytes === 0 ? 'muted' : server.swap.used_pct >= 70 ? 'warn' : 'ok'}
            progress={server.swap.total_bytes > 0 ? server.swap.used_pct : undefined}
          />
        </Section>

        <Section title="Diskler ve Bağlı Alanlar" icon={<HardDrive size={12} />}>
          {(server.disks?.length ? server.disks : [{ ...server.disk, type: '', available: true }]).map(disk => (
            <MetricCard key={disk.mountpoint}
              icon={<HardDrive size={14} />}
              label={disk.mountpoint}
              value={disk.available ? `${fmtBytes(disk.used_bytes)} / ${fmtBytes(disk.total_bytes)}` : 'Erişilemiyor'}
              sub={`${disk.filesystem}${disk.type ? ` · ${disk.type}` : ''}${disk.available ? ` · ${fmtBytes(disk.available_bytes)} boş · %${disk.used_pct} dolu` : ''}`}
              status={!disk.available ? 'warn' : disk.used_pct >= 90 ? 'error' : disk.used_pct >= 75 ? 'warn' : 'ok'}
              progress={disk.available ? disk.used_pct : undefined}
            />
          ))}
          {server.disk_source === 'process' && <p className="col-span-full text-xs text-amber-600">İşlem ortamındaki bağlı alanlar gösteriliyor. Ana sunucunun tüm diskleri için host bağlantısı etkin olmalıdır.</p>}
        </Section>

        <Section title="Disk I/O" icon={<TrendingUp size={12} />}>
          <MetricCard
            icon={<TrendingUp size={14} />}
            label="Toplam Okuma"
            value={fmtBytes(readBytes)}
            sub={`${fmt(server.io.reads_completed)} okuma işlemi`}
            status="muted"
          />
          <MetricCard
            icon={<TrendingUp size={14} />}
            label="Toplam Yazma"
            value={fmtBytes(writeBytes)}
            sub={`${fmt(server.io.writes_completed)} yazma işlemi`}
            status="muted"
          />
          <MetricCard
            icon={<AlertTriangle size={14} />}
            label="I/O Kuyruğu"
            value={server.io.io_in_progress}
            sub={`Toplam I/O süresi ${fmtDuration(Math.round(server.io.io_time_ms / 1000))}`}
            status={server.io.io_in_progress > 0 ? 'warn' : 'ok'}
          />
          <MetricCard
            icon={<Clock size={14} />}
            label="Disk Cihazı"
            value={server.io.device}
            sub={`Weighted I/O ${(server.io.weighted_io_time_ms / 1000).toFixed(1)}s`}
            status="info"
          />
        </Section>
      </Panel>
    </div>
  );
}
